import React, { useState, useContext, useEffect, useRef } from "react"
import { Link } from "react-router-dom"

import { UserContext } from "../context/userContext"
import { FirebaseContext } from "../firebase"
import _ from "lodash"

import StaffProfilePhoto from "../pages/Staff/components/StaffProfilePhoto"

// Components
import Notifications from "../components/notifications/Notifications"
import Appointments from "../components/Appointments/Appointments"
import DailyPlanner from "../components/DailyPlanner/DailyPlanner"
import Requests from "../pages/Documents/RequestsManagement/index"
import HousingComponent from "../components/Housing/Housing"
import CallStatus from "../components/CallStatus"

// Hooks
import useLabels from "../hooks/useLabels"

// Prime
import { Dialog } from "primereact/dialog"

// assets
import logo from "../assets/images/logo-white-text.png"
import DeveloperTools from "../components/DeveloperTools/DeveloperTools"

import SelectorContainer from "../nav/NavMain/Selectors/SelectorContainer"
import { gql, useQuery } from "@apollo/client"
import { ORGANIZATION_VOLUNTEER_ROLE } from "../helpers/staffRoleHelpers"

// GraphQL query
const STAFF_MEMBERSHIP_ORGANIZATION = gql`
  query getStaffMembershipDetailByStaffID(
    $staff_id: ID!
    $organization_id: ID
    $staff_membership_id: ID!
  ) {
    getStaffMembershipDetailByStaffID(
      staff_id: $staff_id
      organization_id: $organization_id
    ) {
      membership_list
    }
    userAdmin: staffDataByStaffMembershipID(
      staff_membership_id: $staff_membership_id
    ) {
      organization_id
    }
  }
`

export default function Header() {
  const user = useContext(UserContext)
  const firebase = useContext(FirebaseContext)
  const [loadingLabels, labels] = useLabels(["intake_session"])
  const [organizationName, setOrganizationName] = useState("")

  // States
  const [toggles, setToggles] = useState({
    profile: false,
    documents: false,
    telerecovery: false,
    documentSigning: false,
    housing: false,
    coordinationBoards: false,
    organizationMembership: false,
  })

  const [currentStaffMembership, setCurrentStaffMembership] = useState("")
  const [staffAdminMembershipOrg, setStaffAdminMembershipOrg] = useState("")

  let profileMenuRef = useRef()

  const { data, loading, error, refetch } = useQuery(
    STAFF_MEMBERSHIP_ORGANIZATION,
    {
      variables: {
        staff_id: user.session.user.staff_id,
        organization_id: user.session.user.organization_id,
        staff_membership_id: user.session.user.staff_membership_id,
      },
    }
  )
  useEffect(() => {
    refetch()
  }, [user.session.user.organization_id, refetch])

  useEffect(() => {
    setStaffAdminMembershipOrg(data?.userAdmin?.organization_id)

    if (data?.getStaffMembershipDetailByStaffID?.membership_list[0]) {
      setCurrentStaffMembership(
        data.getStaffMembershipDetailByStaffID.membership_list[0]
      )
      setToggles((prev) => ({ ...prev, organizationMembership: true }))
    } else {
      setToggles((prev) => ({ ...prev, organizationMembership: false }))
      setCurrentStaffMembership("")
    }
  }, [data, user.session.user.organization_id])

  useEffect(() => {
    const microservicesRef = firebase.db
      .collection("enterprise-config")
      .doc(user.session.user.enterprise_id)
      .collection("organizations")
      .doc(user.session.user.organization_id)
      .collection("microservices")

    const unsubscribe = microservicesRef.onSnapshot(
      (snapshot) => {
        if (!snapshot.empty) {
          const microservicesData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))

          const updateToggles = {
            telerecovery: !!microservicesData.find(
              (service) => service.id === "1"
            )?.enable,
            documentSigning: !!microservicesData.find(
              (service) => service.id === "8"
            )?.enable,
            housing: !!microservicesData.find((service) => service.id === "7")
              ?.enable,
            coordinationBoards: !!microservicesData.find(
              (service) => service.id === "14"
            )?.enable,
          }

          setToggles((prev) => ({ ...prev, ...updateToggles }))
        } else {
          console.log("No microservices found!")
        }
      },
      (error) => {
        console.log("Error listening to microservices collection:", error)
      }
    )

    return () => unsubscribe()
  }, [user.session.user.enterprise_id, user.session.user.organization_id])

  useEffect(() => {
    const profileHandler = (e) => {
      if (!profileMenuRef.current.contains(e.target)) {
        setToggles((prev) => ({ ...prev, profile: false }))
      }
    }

    document.addEventListener("mousedown", profileHandler)

    return () => {
      document.removeEventListener("mousedown", profileHandler)
    }
  }, [])

  useEffect(() => {
    const organizationRef = firebase.db
      .collection("enterprise-config")
      .doc(user.session.user.enterprise_id)
      .collection("organizations")
      .doc(user.session.user.organization_id)

    const unsubscribe = organizationRef.onSnapshot(
      (snapshot) => {
        if (snapshot.exists) {
          const data = snapshot.data()
          setOrganizationName(data.organization_name || "") // Ensure a default value
        }
      },
      (error) => {
        console.log("Error listening to organizations collection:", error)
      }
    )

    return () => unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.session.user.enterprise_id, user.session.user.organization_id])

  useEffect(() => {
    if (user.triggerDocumentRequestShow !== undefined) {
      setToggles((prev) => ({ ...prev, documents: true }))
    }
  }, [user.triggerDocumentRequestShow])

  // Event handler
  const handleReferralNetwork = () => {
    if (user.session.user.staff_role <= 6) {
      return (
        <li className="top-dropdown-option">
          <Link to="/referral-network">
            <span className="material-icons">group</span>
            <label>Referral Network</label>
          </Link>
        </li>
      )
    }
  }

  if (loadingLabels || loading) return null

  return (
    <>
      <header>
        <div className="wrapper">
          <Link to="/">
            <img src={logo} alt="Recovery Link Logo" className="logo" />
          </Link>
          <div className="header-containers">
            <SelectorContainer flexDirection="row" />
          </div>

          <ul className="nav-top">
            <DeveloperTools />
            <CallStatus />
            <Notifications />

            {user.session.user.staff_role < 9 && (
              <>
                <li className="top-icon">
                  <Link to="/">
                    <span className="material-icons top-icon-span">
                      dashboard
                    </span>
                    <label className="top-menu-label">Dashboard</label>
                  </Link>
                </li>
                <DailyPlanner />
                <li
                  className="top-icon"
                  onClick={() =>
                    user.setToggleAppointments({
                      toggle: true,
                      page: "default",
                    })
                  }
                  onKeyDown={() =>
                    user.setToggleAppointments({
                      toggle: true,
                      page: "default",
                    })
                  }
                >
                  <span className="material-icons top-icon-span">
                    calendar_month
                  </span>
                  <label className="top-menu-label">Calendar</label>
                </li>
                <Appointments />
                <li className="top-icon">
                  <span className="material-icons top-icon-span">apps</span>
                  <label className="top-menu-label">Quick Actions</label>
                  <ul className="top-menu-dropdown">
                    {user.session.user.staff_role !== ORGANIZATION_VOLUNTEER_ROLE && ( // Not allowed for Volunteer role
                      <li className="top-dropdown-option">
                        <Link to="form/intake_session/1/">
                          <span className="material-icons">person_add</span>
                          <label>{labels.intake_session}</label>
                        </Link>
                      </li>
                    )}
                    {handleReferralNetwork()}
                    {toggles.documentSigning && (
                      <li
                        className="top-dropdown-option"
                        onClick={() =>
                          setToggles((prev) => ({ ...prev, documents: true }))
                        }
                        onKeyDown={() =>
                          setToggles((prev) => ({ ...prev, documents: true }))
                        }
                      >
                        <span className="material-icons">text_snippet</span>
                        <label>Documents</label>
                      </li>
                    )}
                    {toggles.telerecovery && (
                      <li className="top-dropdown-option">
                        <Link to="/global-messages">
                          <span className="material-icons">chat</span>
                          <label>Messages</label>
                        </Link>
                      </li>
                    )}
                    {toggles.housing && (
                      <li className="top-dropdown-option">
                        <HousingComponent />
                      </li>
                    )}
                    <li className="top-dropdown-option">
                      <Link to="/coordination-board">
                        <span className="material-icons">grid_view</span>
                        <label>Coordination Boards</label>
                      </Link>
                    </li>
                    {toggles.telerecovery && (
                      <li className="top-dropdown-option">
                        <Link to="/external-calls">
                          <span className="material-icons">phone</span>
                          <label>External Calls</label>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              </>
            )}
            {user.session.user.staff_role >= 9 && (
              <>
                <li className="top-icon">
                  <span className="material-icons top-icon-span">apps</span>
                  <label className="top-menu-label">Quick Actions</label>
                  <ul className="top-menu-dropdown">
                    <li className="top-dropdown-option">
                      <Link to="/coordination-board">
                        <span className="material-icons">grid_view</span>
                        <label>Coordination Boards</label>
                      </Link>
                    </li>
                  </ul>
                </li>
              </>
            )}
            <li className="top-icon">
              <a
                href="https://help.myrecoverylink.com/"
                target="_blank"
                rel="noreferrer"
              >
                <span className="material-icons top-icon-span">help</span>
                <label className="top-menu-label">Support</label>
              </a>
            </li>
          </ul>
          <div className="profile-menu" ref={profileMenuRef}>
            <div
              className="profile-menu-trigger"
              id="profile-menu-trigger"
              onClick={() =>
                setToggles((prev) => ({ ...prev, profile: !prev.profile }))
              }
            >
              <StaffProfilePhoto
                staff_profile_image={user.session.user.staff_profile_image}
                className="border-2 border-white rounded-full w-11 h-11"
              />
              <div className="summary">
                <span>
                  {_.unescape(user.session.user.staff_name_first)}{" "}
                  {_.unescape(user.session.user.staff_name_last)}
                </span>
                <span>{user.session.user.staff_description}</span>
                <span>{_.unescape(organizationName)}</span>
              </div>
            </div>
            <ul
              id="top-main-menu"
              className={
                toggles.profile
                  ? "top-menu-dropdown-profile active"
                  : "top-menu-dropdown-profile"
              }
            >
              {user.session.user.staff_role < 9 && (
                <>
                  {toggles.organizationMembership && (
                    <li
                      className="top-dropdown-option"
                      onClick={() =>
                        user.setToggleAppointments({
                          toggle: true,
                          page: "availability",
                        })
                      }
                    >
                      <span className="material-icons">calendar_month</span>
                      <label>My Availability</label>
                    </li>
                  )}
                  {toggles.organizationMembership && (
                    <li className="top-dropdown-option">
                      <Link
                        to="/organization/staff/profile"
                        onClick={() =>
                          user.changeCurrentStaffMembershipId(
                            currentStaffMembership
                          )
                        }
                      >
                        <span className="material-icons">fact_check</span>
                        <label>Staff Member profile</label>
                      </Link>
                    </li>
                  )}
                </>
              )}
              <li className="top-dropdown-option">
                <Link to="account/2fa/settings">
                  <span className="material-icons"> key </span>
                  <label>2FA Settings</label>
                </Link>
              </li>
              <li className="top-dropdown-option">
                <Link
                  to={`/form/account_preferences/1/${user.session.user.staff_id_stamp}`}
                  onClick={() => {
                    if (
                      user.session.user.staff_role === 0 ||
                      user.session.user.staff_role === 1 ||
                      user.session.user.staff_role === 2
                    ) {
                      user.changeOrganization(staffAdminMembershipOrg)
                      user.changeCurrentStaffMembershipId(
                        user.session.user.staff_membership_id
                      )
                    } else {
                      user.changeCurrentStaffMembershipId(
                        currentStaffMembership
                      )
                    }
                  }}
                >
                  <span className="material-icons"> manage_accounts </span>
                  <label>Account Preferences</label>
                </Link>
              </li>
              <div className="divider dropdown"></div>
              <li
                className="top-dropdown-option logout"
                onClick={() => {
                  firebase.signOut()
                }}
              >
                <span className="material-icons"> power_settings_new </span>
                <label>Logout</label>
              </li>
            </ul>
          </div>
        </div>
      </header>
      <Dialog
        visible={toggles.documents}
        onHide={() => setToggles((prev) => ({ ...prev, documents: false }))}
        breakpoints={{ "960px": "75vw", "640px": "100vw" }}
        style={{ width: "70vw" }}
        header="Documents"
      >
        <Requests />
      </Dialog>
    </>
  )
}
